// src/pages/Users.js
import React, { useEffect, useState } from "react";
import api from "../services/api.js";
import { Users as Pelanggan, PencilIcon } from "lucide-react";
import { Dialog } from "@headlessui/react";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [, setEditUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get(`/users`, {
          headers: { authorization: token },
        });
        if (res.data.status === 0) {
          setUsers(res.data.data);
        } else {
          setError("Gagal mengambil data user.");
        }
      } catch (err) {
        setError("Terjadi kesalahan saat memuat data.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [token]);

  const handleEdit = (user) => {
    setEditUser(user);
    setFormData({
      id: user.id,
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      whatsapp: user.whatsapp || "",
      telp: user.telp || "",
      status: user.status || "active",
      group_id: user.group_id || null,
      member_id: user.member_id || null,
      email: user.email || "",
    });
    setIsModalOpen(true);
  };

  const handleUpdate = async () => {
    try {
      const { id, ...dataToUpdate } = formData;
      const requiredFields = [
        "first_name",
        "last_name",
        "whatsapp",
        "telp",
        "status",
        "group_id",
        "member_id",
        "email",
      ];

      const cleanedData = {};
      requiredFields.forEach((key) => {
        cleanedData[key] =
          dataToUpdate[key] === undefined || dataToUpdate[key] === ""
            ? null
            : dataToUpdate[key];
      });

      const res = await api.put(`/users/${id}`, cleanedData, {
        headers: { authorization: token },
      });

      if (res.data.status === 0) {
        alert("User berhasil diperbarui.");
        setEditUser(null);
        setIsModalOpen(false);
        setUsers((prev) =>
          prev.map((u) => (u.id === id ? { ...u, ...cleanedData } : u))
        );
      } else {
        alert("Gagal memperbarui user.");
      }
    } catch (err) {
      console.error("Error saat update user:", err);
      alert("Terjadi kesalahan saat update user.");
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditUser(null);
    setFormData({});
  };

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold flex items-center gap-2 mb-6">
        <Pelanggan /> Daftar Pengguna
      </h1>

      <div className="overflow-x-auto rounded-xl shadow bg-white dark:bg-gray-800">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="p-3 text-left font-semibold">Nama</th>
              <th className="p-3 text-left font-semibold">Email</th>
              <th className="p-3 text-left font-semibold">Grup</th>
              <th className="p-3 text-left font-semibold">Status</th>
              <th className="p-3 text-left font-semibold">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.email} className="border-t dark:border-gray-700">
                <td className="p-3">
                  {user.first_name} {user.last_name}
                </td>
                <td className="p-3">{user.email}</td>
                <td className="p-3">{user.group_name}</td>
                <td className="p-3">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.status === "active"
                        ? "bg-green-500 text-white"
                        : user.status === "inactive"
                        ? "bg-yellow-500 text-white"
                        : user.status === "closed"
                        ? "bg-gray-500 text-white"
                        : "bg-red-500 text-white"
                    }`}
                  >
                    {user.status}
                  </span>
                </td>
                <td className="p-3">
                  <button
                    onClick={() => handleEdit(user)}
                    className="flex items-center gap-1 px-3 py-2 text-blue-600 hover:text-blue-800 rounded-md border border-blue-600 hover:border-blue-800 text-sm transition"
                  >
                    <PencilIcon size={16} /> Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        className="fixed z-50 inset-0 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen px-4">
          <Dialog.Panel className="bg-white dark:bg-gray-600 p-6 rounded-2xl shadow-xl w-full max-w-md">
            <Dialog.Title className="text-xl font-bold text-black dark:text-white mb-6 text-center">
              Edit Pengguna
            </Dialog.Title>
            <div className="space-y-4">
              <input
                className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                value={formData.first_name || ""}
                onChange={(e) =>
                  setFormData({ ...formData, first_name: e.target.value })
                }
                placeholder="First Name"
              />
              <input
                className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                value={formData.last_name || ""}
                onChange={(e) =>
                  setFormData({ ...formData, last_name: e.target.value })
                }
                placeholder="Last Name"
              />
              <input
                className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                value={formData.email || ""}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="Email"
              />
              <input
                className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                value={formData.whatsapp || ""}
                onChange={(e) =>
                  setFormData({ ...formData, whatsapp: e.target.value })
                }
                placeholder="WhatsApp"
              />
              <input
                className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                value={formData.telp || ""}
                onChange={(e) =>
                  setFormData({ ...formData, telp: e.target.value })
                }
                placeholder="Telepon"
              />
              <select
                className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                value={formData.status || "active"}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="closed">Closed</option>
                <option value="deleted">Deleted</option>
              </select>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  onClick={handleUpdate}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg"
                >
                  Simpan
                </button>
                <button
                  onClick={closeModal}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-5 py-2 rounded-lg"
                >
                  Batal
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default Users;
